import { isSome } from '@mbfab/shared'
import { Check, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import type { ReactNode } from 'react'

import type { UnmatchedMillsheetViewModel, UpdateOrderNumberResult } from './unmatched-millsheet-types'

import type { DataTableColumn } from '@/components/ui/data-table/data-table'
import { DataTable } from '@/components/ui/data-table/data-table'
import { valueOrDash } from '@/components/ui/data-table/value-or-dash'
import { notify } from '@/lib/notify'
import { cn } from '@/lib/utils'

// 注文番号の保存結果を機械可読な形で残す（v1 のトースト3分岐を DOM に残る feedback に置き換える。
// docs/develop/client_ui/ui_verification.md「操作結果を機械可読で確定できるようにする」）
function feedbackFor(result: UpdateOrderNumberResult): { tone: 'success' | 'warning' | 'error'; message: string } {
  switch (result.status) {
    case 'linked':
      return { tone: 'success', message: '発注に紐付けました' }
    case 'clearedUnlinked':
      return { tone: 'success', message: '注文番号を空にしました' }
    case 'savedUnmatched':
      return {
        tone: 'warning',
        message:
          '該当する発注データが見つからないため未紐付けのまま保存しました（発注データが取り込まれた時点で自動的に紐付きます）',
      }
    case 'error':
      return { tone: 'error', message: result.message }
  }
}

const feedbackToneClass: Record<'success' | 'warning' | 'error', string> = {
  success: 'text-success',
  warning: 'text-warning',
  error: 'text-destructive',
}

// 注文番号列のセル。行ごとに編集中かどうかの状態を持つため、DataTable の column.render
// （関数コンポーネントではない）から直接フックを使えるよう、行単位のコンポーネントとして切り出す
export function OrderNumberCell({
  row,
  onUpdateOrderNumber,
}: {
  row: UnmatchedMillsheetViewModel
  onUpdateOrderNumber: (millsheetId: string, orderNumber: string) => Promise<UpdateOrderNumberResult>
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(row.orderNumber)
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<UpdateOrderNumberResult | null>(null)

  const startEditing = () => {
    setDraft(row.orderNumber)
    setFeedback(null)
    setIsEditing(true)
  }

  const save = async () => {
    setSaving(true)
    try {
      const result = await onUpdateOrderNumber(row.id, draft.trim())
      setFeedback(result)
      setIsEditing(false)
      // 既存の feedback 表示（tone/message）とトーストの文言を揃える（DOM 残存表示と同じ根拠を出す）
      const { tone, message } = feedbackFor(result)
      if (tone === 'success') {
        notify.success(message)
      } else if (tone === 'warning') {
        notify.warning(message)
      } else {
        notify.error(message)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid={`unlinked-millsheet-order-number-${row.no}`}>
      {isEditing ? (
        <div className="flex items-center gap-1.5">
          <input
            autoFocus
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
            }}
            onKeyDown={(event) => {
              if (event.nativeEvent.isComposing || event.keyCode === 229) {
                return
              }
              if (event.key === 'Enter') {
                void save()
              }
              if (event.key === 'Escape') {
                setIsEditing(false)
              }
            }}
            disabled={saving}
            className="h-8 w-44 rounded-md border border-primary bg-card px-2 text-sm outline-none ring-2 ring-primary/20"
            aria-label="注文番号"
            data-testid="unlinked-millsheet-order-number-input"
          />
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              void save()
            }}
            data-testid="unlinked-millsheet-save"
            className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground disabled:opacity-50"
            aria-label="保存"
          >
            <Check className="size-4" />
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              setIsEditing(false)
            }}
            className="flex size-8 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted"
            aria-label="キャンセル"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1.5">
            <span className="min-w-0 flex-1 truncate font-medium text-foreground">{valueOrDash(row.orderNumber)}</span>
            <button
              type="button"
              onClick={startEditing}
              aria-label={`${row.no} の注文番号を編集`}
              title="注文番号を編集"
              data-testid={`unlinked-millsheet-edit-${row.no}`}
              className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground/70 transition-colors hover:bg-accent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              <Pencil className="size-3.5" />
            </button>
          </div>
          {isSome(feedback) && (
            <span
              className={cn('text-xs', feedbackToneClass[feedbackFor(feedback).tone])}
              data-testid={`unlinked-millsheet-feedback-${row.no}`}
              data-status={feedback.status}
            >
              {feedbackFor(feedback).message}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

type MillsheetItem = UnmatchedMillsheetViewModel['items'][number]

const ITEM_COLUMNS: Array<DataTableColumn<MillsheetItem>> = [
  { key: 'size', title: 'サイズ', render: (item): ReactNode => valueOrDash(item.size) },
  { key: 'length', title: '長さ', render: (item): ReactNode => valueOrDash(item.length) },
  { key: 'quantity', title: '員数', numeric: true, render: (item): ReactNode => valueOrDash(item.quantity) },
  {
    key: 'weight',
    title: '重量(kg)',
    numeric: true,
    render: (item): ReactNode => valueOrDash(item.weight?.toLocaleString()),
  },
  { key: 'heatNumber', title: '製鋼番号', render: (item): ReactNode => valueOrDash(item.heatNumber) },
]

// 展開行の明細テーブル（サイズ/長さ/員数/重量/製鋼番号）。DataTable の expandedRowRender から呼ぶ
export function MillsheetItemsTable({ row }: { row: UnmatchedMillsheetViewModel }) {
  return (
    <DataTable
      columns={ITEM_COLUMNS}
      rows={row.items}
      rowKey={(item) => item.id}
      testIdPrefix="unlinked-millsheet-items"
      emptyMessage="明細がありません"
    />
  )
}
